import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Add as AddIcon, Delete as DeleteIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { api, FeatureAccessMapping, MappableTraining } from '../../services/api';

interface FeatureAccessTabProps {
  open: boolean;
}

type ToastState = { severity: 'success' | 'error'; message: string } | null;

/** Friendly labels for the known feature codes; falls back to the raw code. */
const FEATURE_LABELS: Record<string, string> = {
  'SUPER-BUYING-CAL': 'Super Buying Calculator',
  'SUPER-SELLING-CAL': 'Super Selling Calculator',
  'PRICE-PROMO': 'Price Promo',
  'NEARBY-DEAL': 'Nearby Deals',
  'SUPER-RATIO': 'Super Ratio',
  'SUPER-PICKER-CHAT': 'Super Picker Chat',
};

function featureLabel(feature: string): string {
  return FEATURE_LABELS[feature] || feature;
}

function formatDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-SG', { year: 'numeric', month: 'short', day: '2-digit' });
}

function trainingOptionLabel(t: MappableTraining): string {
  const date = formatDate(t.startDate);
  const name = t.eventName || `Training #${t.trainingId}`;
  return date ? `${name} (${date})` : name;
}

/**
 * Admin tab for mapping training events to app features. A single training can
 * unlock more than one feature. Agents who attended a mapped training gain
 * access automatically. Soft-deletes on remove; mappings are idempotent per
 * (training, feature).
 */
const FeatureAccessTab: React.FC<FeatureAccessTabProps> = ({ open }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [mappings, setMappings] = useState<FeatureAccessMapping[]>([]);
  const [trainings, setTrainings] = useState<MappableTraining[]>([]);
  const [features, setFeatures] = useState<string[]>([]);
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>([]);
  const [selectedTraining, setSelectedTraining] = useState<MappableTraining | null>(null);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [removingId, setRemovingId] = useState<number | null>(null);
  const [toast, setToast] = useState<ToastState>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.listFeatureAccess();
      setMappings(data.mappings);
      setTrainings(data.trainings);
      setFeatures(data.features);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load feature access mappings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      void load();
    }
  }, [open, load]);

  // Which features each training is already mapped to — used to flag existing
  // mappings in the picker (the backend is idempotent regardless).
  const mappedFeaturesByTraining = useMemo(() => {
    const map = new Map<number, Set<string>>();
    for (const m of mappings) {
      const set = map.get(m.trainingId) || new Set<string>();
      set.add(m.feature);
      map.set(m.trainingId, set);
    }
    return map;
  }, [mappings]);

  const mappingsByFeature = useMemo(() => {
    const grouped = new Map<string, FeatureAccessMapping[]>();
    for (const m of mappings) {
      const list = grouped.get(m.feature) || [];
      list.push(m);
      grouped.set(m.feature, list);
    }
    return grouped;
  }, [mappings]);

  const toggleFeature = useCallback((feature: string) => {
    setSelectedFeatures((prev) =>
      prev.includes(feature) ? prev.filter((f) => f !== feature) : [...prev, feature],
    );
  }, []);

  const handleAdd = useCallback(async () => {
    if (!selectedTraining || selectedFeatures.length === 0) return;
    setSubmitting(true);
    try {
      for (const feature of selectedFeatures) {
        // eslint-disable-next-line no-await-in-loop
        await api.createFeatureAccess({ trainingId: selectedTraining.trainingId, feature });
      }
      const count = selectedFeatures.length;
      setSelectedTraining(null);
      setSelectedFeatures([]);
      setToast({ severity: 'success', message: `${count} mapping${count > 1 ? 's' : ''} saved` });
      await load();
    } catch (err) {
      setToast({
        severity: 'error',
        message: err instanceof Error ? err.message : 'Failed to add mappings',
      });
    } finally {
      setSubmitting(false);
    }
  }, [selectedTraining, selectedFeatures, load]);

  const handleRemove = useCallback(
    async (mapping: FeatureAccessMapping) => {
      const confirmed = window.confirm(
        `Remove ${featureLabel(mapping.feature)} access for "${mapping.eventName || `Training #${mapping.trainingId}`}"?`,
      );
      if (!confirmed) return;
      setRemovingId(mapping.id);
      try {
        await api.deleteFeatureAccess(mapping.id);
        setToast({ severity: 'success', message: 'Mapping removed' });
        await load();
      } catch (err) {
        setToast({
          severity: 'error',
          message: err instanceof Error ? err.message : 'Failed to remove mapping',
        });
      } finally {
        setRemovingId(null);
      }
    },
    [load],
  );

  return (
    <Box sx={{ minHeight: 420 }}>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        Map a training to one or more features. Any agent who attended the training (scanned in)
        automatically gains access to those features — no role assignment needed.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Add-mapping controls: pick a training, then toggle the features it unlocks. */}
      <Stack spacing={1.5} sx={{ mb: 3 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'stretch', sm: 'center' }}
        >
          <Autocomplete
            size="small"
            options={trainings}
            value={selectedTraining}
            onChange={(_, value) => setSelectedTraining(value)}
            getOptionLabel={trainingOptionLabel}
            isOptionEqualToValue={(a, b) => a.trainingId === b.trainingId}
            sx={{ flex: 1, minWidth: { xs: '100%', sm: 320 } }}
            renderOption={(props, option) => {
              const mapped = mappedFeaturesByTraining.get(option.trainingId);
              return (
                <li {...props} key={option.trainingId}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      {trainingOptionLabel(option)}
                    </Typography>
                    {mapped &&
                      Array.from(mapped).map((f) => (
                        <Chip
                          key={f}
                          label={featureLabel(f)}
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                      ))}
                  </Box>
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField {...params} label="Training" placeholder="Search training…" />
            )}
          />

          <Button
            variant="contained"
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
            disabled={submitting || !selectedTraining || selectedFeatures.length === 0}
            onClick={handleAdd}
            sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
          >
            Add mapping
          </Button>
        </Stack>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary', mr: 0.5 }}>
            Features:
          </Typography>
          {features.map((f) => {
            const selected = selectedFeatures.includes(f);
            const alreadyMapped = selectedTraining
              ? mappedFeaturesByTraining.get(selectedTraining.trainingId)?.has(f)
              : false;
            return (
              <Chip
                key={f}
                label={alreadyMapped ? `${featureLabel(f)} ✓` : featureLabel(f)}
                onClick={() => toggleFeature(f)}
                color={selected ? 'primary' : 'default'}
                variant={selected ? 'filled' : 'outlined'}
                sx={{ fontWeight: 600 }}
              />
            );
          })}
        </Box>
      </Stack>

      {/* Current mappings, grouped by feature (a training may appear under several). */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Current mappings
        </Typography>
        <Tooltip title="Refresh">
          <span>
            <IconButton size="small" onClick={() => void load()} disabled={loading}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : mappings.length === 0 ? (
        <Typography variant="body2" sx={{ color: 'text.secondary', py: 3 }}>
          No mappings yet. Add one above.
        </Typography>
      ) : (
        <Stack spacing={2}>
          {features
            .filter((f) => (mappingsByFeature.get(f) || []).length > 0)
            .map((f) => (
              <Box key={f}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                  {featureLabel(f)}
                </Typography>
                <TableContainer
                  sx={{ border: '1px solid rgba(var(--color-text-rgb), 0.1)', borderRadius: 1 }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Training</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(mappingsByFeature.get(f) || []).map((m) => (
                        <TableRow key={m.id} hover>
                          <TableCell>{m.eventName || `Training #${m.trainingId}`}</TableCell>
                          <TableCell>{formatDate(m.startDate) || '-'}</TableCell>
                          <TableCell>{m.type || '-'}</TableCell>
                          <TableCell align="right">
                            <Tooltip title="Remove">
                              <span>
                                <IconButton
                                  size="small"
                                  color="error"
                                  disabled={removingId === m.id}
                                  onClick={() => void handleRemove(m)}
                                >
                                  {removingId === m.id ? (
                                    <CircularProgress size={16} color="inherit" />
                                  ) : (
                                    <DeleteIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            ))}
        </Stack>
      )}

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)} sx={{ width: '100%' }}>
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default FeatureAccessTab;
